/**
 * ASCII Art Generator - Matrix 数字雨效果
 *
 * Overlay模式：绿色日文字符下落叠加在ASCII之上
 */

import type { MatrixConfig } from './types'
import { getRandomMatrixChar, clamp } from './helpers'

// ============================================
// Matrix 列状态
// ============================================

/** Matrix 列接口 */
interface MatrixColumn {
  x: number           // x 位置
  y: number           // 当前 y 位置（头部）
  speed: number       // 下落速度
  chars: string[]     // 字符列表
  trailLength: number // 拖尾长度
  charSize: number    // 字符大小
}

// ============================================
// Canvas 应用函数
// ============================================

/**
 * 应用 Matrix 数字雨效果到 Canvas
 * @param canvas 目标画布
 * @param config Matrix 配置
 * @param columns 列状态数组（用于持久化）
 */
export function applyMatrix(
  canvas: HTMLCanvasElement,
  config: MatrixConfig,
  columns: MatrixColumn[] = []
): MatrixColumn[] {
  const ctx = canvas.getContext('2d')!
  const { width, height } = canvas
  const { strength, speed, charSize, trailLength, density, mode, time } = config

  // 初始化或更新列
  if (columns.length === 0 || columns[0].charSize !== charSize) {
    columns = initColumns(width, height, charSize, density, trailLength)
  }

  // 更新列位置
  for (const col of columns) {
    col.y += col.speed * speed * time * 0.5

    // 重置到顶部
    if (col.y > height + col.trailLength * charSize) {
      col.y = -col.trailLength * charSize
      col.chars = generateChars(col.trailLength)
    }
  }

  // 设置字体
  ctx.font = `${charSize}px 'MS Gothic', 'Meiryo', monospace`

  // 根据 mode 选择绘制方式
  if (mode === 'replace') {
    // Replace 模式：先清除画布
    ctx.fillStyle = '#000000'
    ctx.fillRect(0, 0, width, height)
  }

  // 绘制每列字符
  for (const col of columns) {
    for (let i = 0; i < col.chars.length; i++) {
      const charY = col.y - i * charSize

      // 跳过超出画布的字符
      if (charY < -charSize || charY > height + charSize) continue

      // 计算亮度（拖尾效果）
      const brightness = 1 - (i / col.chars.length)
      const alpha = strength * brightness

      // 头部字符高亮
      if (i === 0) {
        ctx.fillStyle = `rgba(200, 255, 200, ${alpha})`
      } else {
        ctx.fillStyle = `rgba(0, 150, 0, ${alpha * 0.7})`
      }

      ctx.fillText(col.chars[i], col.x, charY)
    }
  }

  return columns
}

// ============================================
// 初始化函数
// ============================================

/**
 * 初始化 Matrix 列
 */
function initColumns(
  width: number,
  height: number,
  charSize: number,
  density: number,
  trailLength: number
): MatrixColumn[] {
  const numColumns = Math.floor((width / charSize) * density)
  const columns: MatrixColumn[] = []

  for (let i = 0; i < numColumns; i++) {
    columns.push({
      x: i * charSize * (1 / density), // 根据密度调整间距
      y: Math.random() * height,       // 随机初始位置
      speed: 0.5 + Math.random() * 1.5, // 随机速度
      chars: generateChars(trailLength),
      trailLength: trailLength + Math.floor(Math.random() * 10),
      charSize
    })
  }

  return columns
}

/**
 * 生成随机字符列表
 */
function generateChars(length: number): string[] {
  return Array.from({ length }, () => getRandomMatrixChar())
}

// ============================================
// 类封装（用于动画）
// ============================================

/**
 * Matrix 效果类
 */
export class MatrixEffect {
  private config: MatrixConfig
  private columns: MatrixColumn[] = []
  private lastWidth: number = 0
  private lastHeight: number = 0
  private time: number = 0

  constructor(config: MatrixConfig) {
    this.config = config
  }

  /**
   * 更新时间
   */
  update(deltaTime: number): void {
    this.time += deltaTime
  }

  /**
   * 更新配置
   */
  updateConfig(config: MatrixConfig): void {
    // 如果关键参数变化，重置列
    if (
      config.charSize !== this.config.charSize ||
      config.density !== this.config.density ||
      config.trailLength !== this.config.trailLength
    ) {
      this.columns = []
    }
    this.config = config
  }

  /**
   * 应用效果
   */
  apply(canvas: HTMLCanvasElement): void {
    const { width, height } = canvas

    // 如果尺寸变化，重置列
    if (width !== this.lastWidth || height !== this.lastHeight) {
      this.columns = []
      this.lastWidth = width
      this.lastHeight = height
    }

    const config = { ...this.config, time: this.time }
    this.columns = applyMatrix(canvas, config, this.columns)
  }

  /**
   * 重置效果
   */
  reset(): void {
    this.columns = []
    this.time = 0
  }
}

// ============================================
// 配置工厂函数
// ============================================

/**
 * 从用户设置创建 Matrix 配置
 */
export function createMatrixConfigFromSettings(
  strength: number,
  speed: number,
  charSize: number,
  trailLength: number,
  density: number,
  mode: string
): MatrixConfig {
  return {
    strength: clamp(strength, 0, 1),
    speed: clamp(speed, 0.5, 5),
    charSize: clamp(Math.round(charSize), 10, 30),
    trailLength: clamp(Math.round(trailLength), 5, 30),
    density: clamp(density, 0.1, 1),
    mode: mode as 'overlay' | 'replace',
    time: 0
  }
}

// ============================================
// 默认配置
// ============================================

/** 默认 Matrix 配置 - 参数已调整使效果更明显 */
export const DEFAULT_MATRIX_CONFIG: MatrixConfig = {
  strength: 0.7,
  speed: 1.5,
  charSize: 16,
  trailLength: 20,
  density: 0.6,
  mode: 'overlay',
  time: 0
}